/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Checkbox } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'

/**
 * @interface AlsoSendViaSectionProps
 * @property {boolean} email - Whether the email option is currently checked
 * @property {boolean} push - Whether the push notification option is currently checked
 * @property {(type: 'email' | 'push', checked: boolean) => void} onChange - Callback executed when either checkbox state changes
 */
interface AlsoSendViaSectionProps {
  email: boolean
  push: boolean
  onChange: (type: 'email' | 'push', checked: boolean) => void
}

/**
 * Also Send Via section component - handles email and push notification options
 *
 * This component provides checkboxes for users to select additional delivery methods
 * for their posts beyond the standard feed posting. Users can choose to also send
 * their post content via:
 * - Email notifications to the target audience
 * - Push notifications to mobile devices
 *
 * The component displays a clean interface with labeled checkboxes and handles
 * state changes through a unified onChange callback. When either option is selected,
 * it typically triggers the display of additional role-based visibility controls.
 *
 * The component is memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param props - Component props as defined in AlsoSendViaSectionProps
 * @returns JSX element representing email and push notification selection options
 */
const AlsoSendViaSection: React.FC<AlsoSendViaSectionProps> = React.memo(
  ({ email, push, onChange }) => (
    <div
      css={css`
        align-self: stretch;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: flex;
      `}
    >
      <div
        css={css`
          align-self: stretch;
          justify-content: space-between;
          align-items: flex-start;
          display: flex;
        `}
      >
        <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
          Notify Via:
        </Text>
      </div>
      <div
        css={css`
          align-self: stretch;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 8px;
          display: flex;
        `}
      >
        <div
          css={css`
            justify-content: flex-start;
            align-items: center;
            display: flex;
          `}
        >
          <Checkbox
            checked={email}
            onChange={(e) => onChange('email', e.target.checked)}
            style={{ width: 24, height: 24 }}
          />
          <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
            Email
          </Text>
        </div>
        <div
          css={css`
            justify-content: flex-start;
            align-items: center;
            display: flex;
          `}
        >
          <Checkbox
            checked={push}
            onChange={(e) => onChange('push', e.target.checked)}
            style={{ width: 24, height: 24 }}
          />
          <Text
            variant='paragraphSmall'
            weight='medium'
            color={colors.WHITE}
            style='font-size: 14px;'
          >
            Push Notification
          </Text>
        </div>
      </div>
    </div>
  )
)

export default AlsoSendViaSection
